import { hexToRgba, normalizeHex } from './colorUtils';

export const normalizePaint = (paint, fallbackColor = '#111111') => {
  if (paint && typeof paint === 'object') {
    if (paint.type === 'gradient') {
      const gradient = paint;
      const stops = Array.isArray(gradient.stops) && gradient.stops.length >= 2
        ? gradient.stops.map((stop) => ({
            color: normalizeHex(stop.color) || '#FFFFFF',
            alpha: typeof stop.alpha === 'number' ? stop.alpha : 100,
            pos: typeof stop.pos === 'number' ? stop.pos : 0,
          }))
        : [
            { color: normalizeHex(fallbackColor) || '#111111', alpha: 100, pos: 0 },
            { color: '#FFFFFF', alpha: 100, pos: 100 },
          ];
      return {
        type: 'gradient',
        kind: gradient.kind === 'radial' ? 'radial' : 'linear',
        angle: typeof gradient.angle === 'number' ? gradient.angle : 0,
        stops: stops.slice(0, 4).sort((a, b) => a.pos - b.pos),
      };
    }
    if (paint.type === 'solid') {
      return {
        type: 'solid',
        color: normalizeHex(paint.color) || normalizeHex(fallbackColor) || '#111111',
        alpha: typeof paint.alpha === 'number' ? paint.alpha : 100,
      };
    }
  }
  const legacy = normalizeHex(paint) || normalizeHex(fallbackColor) || '#111111';
  return { type: 'solid', color: legacy, alpha: 100 };
};

export const paintFromHex = (hex, alpha = 100) => ({
  type: 'solid',
  color: normalizeHex(hex) || '#111111',
  alpha,
});

const toStopCss = (stop) => {
  const color = hexToRgba(normalizeHex(stop.color) || '#FFFFFF', (stop.alpha ?? 100) / 100);
  return `${color} ${stop.pos ?? 0}%`;
};

export const paintToCss = (paint) => {
  const normalized = normalizePaint(paint);
  if (normalized.type === 'solid') {
    return hexToRgba(normalized.color, (normalized.alpha ?? 100) / 100);
  }
  const stops = normalized.stops.map(toStopCss).join(', ');
  if (normalized.kind === 'radial') {
    return `radial-gradient(circle, ${stops})`;
  }
  return `linear-gradient(${normalized.angle ?? 0}deg, ${stops})`;
};

export const paintToTextStyles = (paint) => {
  const normalized = normalizePaint(paint);
  if (normalized.type === 'solid') {
    return { color: hexToRgba(normalized.color, (normalized.alpha ?? 100) / 100) };
  }
  return {
    color: 'transparent',
    backgroundImage: paintToCss(normalized),
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  };
};

export const extractPaintColors = (paint) => {
  const normalized = normalizePaint(paint);
  if (normalized.type === 'solid') {
    return [normalizeHex(normalized.color)];
  }
  return normalized.stops.map((stop) => normalizeHex(stop.color)).filter(Boolean);
};

export const getTextPaint = (element) =>
  normalizePaint(element?.style?.paint || element?.paint, element?.color || '#111111');

export const getShapeFillPaint = (element) =>
  normalizePaint(
    element?.style?.fillPaint || element?.fillPaint,
    element?.fill || '#e1dccf',
  );

export const getShapeStrokePaint = (element) =>
  normalizePaint(
    element?.style?.strokePaint || element?.strokePaint,
    element?.stroke || '#c9a962',
  );
